import { MealTime } from "@/clients/models/MealTime";
import { env } from "@/constants/env";
import { SmartlookEvents } from "@/constants/smartlook-events";
import { onboardingRoutes } from "@/router/step-routes";
import Smartlook from "smartlook-client";

const normalizePathname = (pathname: string) => {
  if (pathname === "/") return pathname;
  return pathname.replace(/\/+$/, "");
};

const onboardingRoutePathSet = new Set(
  onboardingRoutes.map((route) => normalizePathname(route.path)),
);

let isSmartlookEnabled = false;
let initialisation: Promise<void> | undefined;

const safely = (operation: () => void) => {
  try {
    operation();
  } catch {
    isSmartlookEnabled = false;
    console.warn("[Smartlook] Analytics operation failed");
  }
};

export const initSmartlook = (isEnabled: boolean): Promise<void> => {
  if (initialisation) return initialisation;
  initialisation = Promise.resolve().then(() => {
    if (!isEnabled) return;
    safely(() => {
      Smartlook.init(env.VITE_SMARTLOOK_KEY);
      isSmartlookEnabled = true;
    });
  });
  return initialisation;
};

export const setSmartlookUser = async (userId: string, email: string) => {
  if (!isSmartlookEnabled) return;

  safely(() => {
    Smartlook.identify(userId, { email });
    Smartlook.record({ ips: true, forms: true });
  });
};

export const setSmartlookPage = (pathname: string) => {
  if (!isSmartlookEnabled) return;

  safely(() => {
    Smartlook.navigation(pathname);
  });
};

export const setSmartlookEvent = (
  event: string,
  data: Record<string, string | number | boolean>,
) => {
  if (!isSmartlookEnabled) return;

  safely(() => {
    Smartlook.track(event, data);
  });
};

/**
 * Track onboarding page
 * @param pathname - The pathname of the page
 */
export const trackOnboardingPage = (pathname: string) => {
  const normalizedPathname = normalizePathname(pathname);
  if (!onboardingRoutePathSet.has(normalizedPathname)) return;

  setSmartlookEvent(`${SmartlookEvents.ONBOARDING_NAVIGATION}_${normalizedPathname}`, {
    pathname: normalizedPathname,
  });
};

/**
 * Track navigation meal time tab
 * @param pathname - The pathname of the page
 */
export const trackNavigationMealTime = (mealTime: MealTime) => {
  switch (mealTime) {
    case MealTime.BREAKFAST:
      setSmartlookEvent(SmartlookEvents.NAVIGATE_TO_BREAKFAST, {});
      break;
    case MealTime.LUNCH:
      setSmartlookEvent(SmartlookEvents.NAVIGATE_TO_LUNCH, {});
      break;
    case MealTime.DINNER:
      setSmartlookEvent(SmartlookEvents.NAVIGATE_TO_DINNER, {});
      break;
    case MealTime.SNACK:
      setSmartlookEvent(SmartlookEvents.NAVIGATE_TO_SNACK, {});
      break;
  }
};
